import * as React from 'react';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import styled, { css } from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { getWidth } from '@components/styles';
import Flex from '@components/layout/flex';
import SquareImg from '@components/basics/artwork-img';
import Typography from '@components/basics/typography';
import Button, { ButtonType } from '@components/actions/button';
import MotionOpacity from '@components/motion/motion-opacity';
import UploadItemProgressBar from './progress-bar';
import {
  UploadImageRequest,
  UploadImageResponse,
} from '@services/merch/types/api/upload-image';
import AppState from '@webapp/store/state';
import { ImageWithPositionPlaceHolder } from '@webapp/store/merch/state';
import { AssetFile } from '@common/types/data/asset-file';

export type statusType = 'uploading' | 'done';

export interface UploadItemProps {
  id?: string;
  className?: string;
  status?: statusType;
  filename?: string;
  onChange: (files: FileList | File[]) => void;
  onCancel: (assetId?: string) => void;
  error?: React.ReactNode;
  noIcon?: boolean;
  inline?: boolean;
  multiple?: boolean;
  inSidebar?: boolean;
  hideUploadOption: boolean;
  locked?: boolean;
  uploaded?: number;
  total?: number;
  accept: string;
  btnDisabled?: boolean;
  isMultiline?: boolean;
  noBorderTop?: boolean;
  noBorderBottom?: boolean;

  isMobile: boolean;
  images:
    | Partial<
        UploadImageRequest &
          ImageWithPositionPlaceHolder &
          UploadImageResponse & {
            asset?: Partial<AssetFile> | undefined;
          }
      >[]
    | undefined;

  title: React.ReactNode;
  label: React.ReactNode;
  dragText?: React.ReactNode;
  content?: React.ReactNode;
  buttonType?: ButtonType;
  icon: React.ReactNode;
  imgUrl: string;
  noWrapper?: boolean;
  progress?: number;
  hasHint?: React.ReactNode;
  multipleFiles?: number;
  handleChangeView?: Function;
  imageView?: string;
  isArt?: boolean;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
  images: state.merch.active?.image,
});

const getTitle = (
  filename?: string,
  uploaded?: number,
  total?: number,
  title?: React.ReactNode
) => {
  if (total && total > 1) {
    return `Uploaded ${uploaded || 0}/${total}`;
  }

  if (filename) {
    return filename;
  }

  if (title) {
    return title;
  }

  return '';
};

export const UploadItem: React.FC<UploadItemProps> = (props) => {
  // const { imageView = 'upload' } = props;
  const [{ isOver, isDragging }, drag] = useDrop({
    accept: [NativeTypes.FILE],
    drop: (item: any) => props.onChange(item.files as File[]),
    collect: (monitor) => {
      return {
        isDragging: !!monitor.canDrop(),
        isOver: !!monitor.isOver(),
      };
    },
  });

  const getBtnText = (status: any) => {
    if (status === 'done' && props.multiple) {
      return <Translate id="UPLOAD.BTN.TEXT.ADD" />;
    }
    switch (status) {
      case 'done':
        return <Translate id="UPLOAD.BTN.TEXT.CHANGE" />;
      case 'uploading':
        return <Translate id="UPLOAD.CANCEL" />;
      default:
        return <Translate id="UPLOAD.BTN.TEXT.BROWSE" />;
    }
  };

  const getButtonType = (status: any): ButtonType => {
    if (props.error) {
      return 'primary';
    }

    if (props.buttonType && !props.error && status !== 'uploading') {
      return props.buttonType;
    }

    if (props.inSidebar && !props.error) {
      return 'secondary-dark';
    }

    if (props.inline && !props.error) {
      return 'secondary-light';
    }

    if (!status || props.multiple) {
      return 'primary';
    }

    if (status === 'uploading') {
      return 'alert-secondary';
    }

    return 'secondary-light';
  };

  const handleButtonClick = () => {
    if (props.status === 'uploading') {
      props.onCancel();
    }
  };

  const handleChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    return ev.target.files?.length ? props.onChange(ev.target.files) : null;
  };

  return (
    <div ref={drag}>
      <Flex
        className={`${props.className} ${props.noWrapper ? 'no-wrapper' : ''} ${
          isDragging && !props.locked ? 'dragging' : ''
        }`}
        flexDirection="column"
      >
        <AnimatePresence>
          {!props.inSidebar && props.content ? props.content : null}
        </AnimatePresence>

        <UploadWrapper inline={props.inline} isMultiline={props.isMultiline}>
          {props.noWrapper ? null : (
            <WrapperText>
              <div>
                <Typography
                  customFont={props.inSidebar ? undefined : 'medium'}
                  type={props.inSidebar ? undefined : 'big-title'}
                  weight={props.inSidebar ? '700' : undefined}
                >
                  {getTitle(
                    props.filename,
                    props.uploaded,
                    props.total,
                    props.title
                  )}
                </Typography>
                <Typography className="upload-item-helper" type="small">
                  {props.label}
                </Typography>
                {!!props.error && (
                  <Typography className="error" weight="700">
                    {props.error}
                  </Typography>
                )}
              </div>
            </WrapperText>
          )}
          {!props.isMobile &&
          !props.inSidebar &&
          props.inline &&
          !props.hideUploadOption ? (
            <UploadButton
              inline={props.inline}
              buttonType={getButtonType(props.status)}
              type="button"
              small
              onClick={handleButtonClick}
              disabled={props.locked || props.btnDisabled}
            >
              {getBtnText(props.status)}
              {props.status !== 'uploading' ? (
                <input
                  type="file"
                  className="file"
                  onChange={handleChange}
                  multiple={!!props.multiple}
                  accept={props.accept}
                />
              ) : null}
            </UploadButton>
          ) : null}
        </UploadWrapper>
        {!props.inSidebar ? (
          <>
            <DropFilesWrapper
              className={`${props.noWrapper ? 'noWrapper' : ''} ${
                props.hasHint || props.noBorderBottom ? 'has-item-below' : ''
              } ${props.noBorderTop ? 'has-item-ontop' : ''} ${
                !!props.error ? 'error' : ''
              }`}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <div>
                  <Typography className="mb-5" weight="700">
                    {props.dragText ? (
                      props.dragText
                    ) : (
                      <Translate id="UPLOAD.DRAG.TITLE" />
                    )}
                  </Typography>
                  {!props.isMobile && props.status !== 'uploading' && (
                    <Typography className="action-subtitle" type="small">
                      <Translate id="UPLOAD.DRAG.TEXT" />
                    </Typography>
                  )}
                  {!props.isMobile && !props.hideUploadOption && (
                    <UploadButton
                      className="button"
                      inline={props.inline}
                      buttonType={getButtonType(props.status)}
                      type="button"
                      onClick={handleButtonClick}
                      disabled={props.locked || props.btnDisabled}
                    >
                      {getBtnText(props.status)}
                      {props.status !== 'uploading' ? (
                        <input
                          type="file"
                          className="file"
                          onChange={handleChange}
                          multiple={!!props.multiple}
                          accept={props.accept}
                          disabled={props.locked || props.btnDisabled}
                        />
                      ) : null}
                    </UploadButton>
                  )}
                </div>
                {props.progress && props.status === 'uploading' ? (
                  <UploadingWrapper>
                    <UploadItemProgressBar progress={props.progress} />
                  </UploadingWrapper>
                ) : props.icon && !props.imgUrl ? (
                  <IconWrapper>
                    <div className="upload-item-icon">{props.icon}</div>
                  </IconWrapper>
                ) : props.imgUrl ? (
                  <MotionOpacity>
                    <SquareImg className="artwork" srcImg={props.imgUrl} />
                  </MotionOpacity>
                ) : null}
              </Flex>
              {!props.hideUploadOption &&
              (props.isMobile || props.inSidebar) ? (
                <UploadButton
                  className="button"
                  inline={props.inline}
                  buttonType={getButtonType(props.status)}
                  type="button"
                  small
                  fullWidth
                  onClick={handleButtonClick}
                  disabled={props.locked || props.btnDisabled}
                >
                  {getBtnText(props.status)}
                  {props.status !== 'uploading' ? (
                    <input
                      type="file"
                      className="file"
                      onChange={handleChange}
                      multiple={!!props.multiple}
                      accept={props.accept}
                    />
                  ) : null}
                </UploadButton>
              ) : null}
              {isDragging && (
                <MotionOpacity>
                  <div className={`drag ${isOver ? 'over' : ''}`} />
                </MotionOpacity>
              )}
            </DropFilesWrapper>
            {props.hasHint ? props.hasHint : null}
          </>
        ) : (
          <UploadButton
            className="button"
            inline={props.inline}
            fullWidth
            buttonType={getButtonType(props.status)}
            type="button"
            small
            onClick={handleButtonClick}
            disabled={props.locked || props.btnDisabled}
          >
            {getBtnText(props.status)}
            {props.status !== 'uploading' ? (
              <input
                id={props.id}
                type="file"
                className="file"
                onChange={handleChange}
                multiple={!!props.multiple}
                accept={props.accept}
              />
            ) : null}
          </UploadButton>
        )}
      </Flex>
    </div>
  );
};

export default connect(mapStateToProps)(styled(UploadItem)`
  position: relative;

  ${(props) =>
    props.inSidebar
      ? css`
          .button {
            max-width: none !important;
          }
        `
      : null}

  ${(props) =>
    props.locked
      ? css`
          pointer-events: none;
          opacity: 0.5;
        `
      : null}


  &.dragging {
    .drag {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      cursor: grabbing;

      .text {
        z-index: 4;
        color: ${({ theme }) => theme.colors.background};
      }

      &:before,
      &:after {
        content: '';
        position: absolute;
        border-radius: 2px;
      }

      &:before {
        top: 20px;
        right: 20px;
        bottom: 20px;
        left: 20px;
        border-radius: 2px;
        border: 1px dashed ${({ theme }) => theme.colors.secondary};
        z-index: 2;
      }

      &:after {
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: ${({ theme }) => theme.colors.primary};
        opacity: 0.5;
        z-index: 1;
      }
    }
  }

  .upload-item-helper {
    margin-top: 5px;
    color: ${({ theme }) => theme.colors.tertiary};
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
  }

  .title-upload {
    word-break: break-word;
  }
`);

const WrapperText = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  width: 100%;
  margin-bottom: 20px;
`;

const UploadButton = styled(Button)<{ inline?: boolean; uploaded?: boolean }>`
  position: relative;
  overflow: hidden;
  width: 100%;
  margin-top: ${({ inline }) => (inline ? '0' : '15px')};

  @media ${({ theme }) => theme.device.laptopL} {
    max-width: ${getWidth(3)};
  }

  .file {
    cursor: pointer;
    position: absolute;
    width: 100%;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  }
`;

const UploadWrapper = styled.div<Partial<UploadItemProps>>`
  display: flex;
  flex-direction: ${(props) => (props.isMultiline ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;

  @media ${({ theme }) => theme.device.laptopL} {
    flex-direction: ${(props) => (props.inline ? 'row' : 'column')};
  }
`;

const DropFilesWrapper = styled.div`
  position: relative;
  padding: 15px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.differentBackground};

  &.no-wrapper {
    margin-top: 0px;
  }

  @media ${({ theme }) => theme.device.laptopL} {
    padding: 20px;
    &.no-wrapper {
      margin-top: 0px;
    }
  }

  &.has-item-ontop {
    border-radius: 0 0 4px 4px;
  }

  .action-subtitle {
    color: ${({ theme }) => theme.colors.tertiary};
  }

  .button {
    margin-top: 20px;
  }

  .artwork {
    width: 100px;
    height: 100px;
  }

  &.error {
    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: auto;
      bottom: 0;
      left: 0;
      width: 3px;
      border-radius: 4px 0 0 4px;
      background-color: ${({ theme }) => theme.colors.error};
    }
  }

  &.has-item-below {
    &.error {
      &:before {
        border-radius: 4px 0 0 0;
      }
    }
  }
`;

const IconWrapper = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.colors.secondary};

  .upload-item-icon {
    max-width: 24px;
    max-height: 24px;
    svg {
      max-width: 24px;
      max-height: 24px;
    }
  }
`;

const UploadingWrapper = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  border: 1px dashed ${({ theme }) => theme.colors.secondary};
`;
